/*
 * SupplierPriceList.jsx — "one supplier → all items with purchasing
 * prices" report. Pick a supplier, pick a lookback window (default
 * 180 d), and get a sortable / searchable table of every item this
 * supplier has billed us for from MSSQL (`GPurci`). Each row also
 * carries a `SAP` pill when a released list-price contract exists for
 * that (supplier, item) pair — so buyers can spot at a glance which
 * items are spot-buys and which are under contract.
 *
 * Purchasing price = last-billed rate, INR-normalised (via each bill's
 * `ExchRate`). This is what actually gets paid, not a standing list
 * price. That distinction is why this report lives separate from
 * `/price-explorer` — the semantics of the primary key (item vs
 * supplier) and the price definition (paid vs contracted) are both
 * different.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Home, Search, Loader2, ArrowUp, ArrowDown,
  FileSpreadsheet, ExternalLink,
} from "lucide-react";
import PriceHistoryDrawer from "@/components/PriceHistoryDrawer";
import { fmtDate } from "@/lib/flags";
import api from "@/lib/api";

const inr = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `₹${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const LOOKBACK_OPTIONS = [
  { d: 90,  label: "Last 90 days"  },
  { d: 180, label: "Last 180 days" },
  { d: 365, label: "Last 1 year"   },
  { d: 730, label: "Last 2 years"  },
];

// ---------------------------------------------------------------------
// SupplierPicker — small autocomplete over `/list-prices/all-suppliers`.
// Inline so we don't grow shared component surface for a single caller.
// ---------------------------------------------------------------------
function SupplierPicker({ value, name, onPick }) {
  const [q, setQ] = useState(value ? `${value}${name ? " · " + name : ""}` : "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const timer = useRef(null);
  const boxRef = useRef(null);

  const runSearch = (query) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(
          `/list-prices/all-suppliers?q=${encodeURIComponent(query)}&limit=25`);
        setOpts(r.data?.rows || []);
        setActiveIdx(-1);
      } catch {
        setOpts([]);
      } finally { setLoading(false); }
    }, 200);
  };

  useEffect(() => { runSearch(""); /* prime */ }, []);
  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const pick = (row) => {
    setQ(`${row.code}${row.name ? " · " + row.name : ""}`);
    setOpen(false);
    onPick(row);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, opts.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); const r = activeIdx >= 0 ? opts[activeIdx] : opts[0]; if (r) pick(r); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef} data-testid="spl-supplier-picker">
      <div className="flex items-center gap-2 border border-slate-300 rounded-md bg-white px-3 h-10 focus-within:ring-2 focus-within:ring-cyan-500/40">
        <Search size={14} className="text-slate-400 shrink-0" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); runSearch(e.target.value); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search supplier by code or name…"
          data-testid="spl-supplier-input"
          className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none px-0 text-sm mono"
        />
        {loading && <Loader2 size={14} className="animate-spin text-slate-400 shrink-0" />}
      </div>
      {open && (
        <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
             data-testid="spl-supplier-drop">
          {opts.length === 0 && !loading && (
            <div className="px-3 py-3 text-xs text-slate-500 text-center">{q ? "No matches" : "Type to search"}</div>
          )}
          {opts.map((row, idx) => (
            <button
              key={row.code}
              type="button"
              onClick={() => pick(row)}
              onMouseEnter={() => setActiveIdx(idx)}
              className={`w-full text-left px-3 py-2 border-b border-slate-50 last:border-0 flex items-baseline gap-2 ${
                idx === activeIdx ? "bg-cyan-50" : "hover:bg-slate-50"
              }`}
              data-testid={`spl-supplier-opt-${row.code}`}
            >
              <span className="mono text-xs font-semibold text-slate-800 shrink-0">{row.code}</span>
              <span className="text-xs text-slate-600 truncate">{row.name || "—"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------
export default function SupplierPriceList() {
  const nav = useNavigate();
  const [supplier, setSupplier] = useState(null);         // { code, name }
  const [lookback, setLookback] = useState(180);
  const [data, setData] = useState(null);                 // API response
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");                          // client-side item filter
  const [sortKey, setSortKey] = useState("total_spend");
  const [sortDir, setSortDir] = useState("desc");
  const [historyItem, setHistoryItem] = useState(null);

  // Fetch whenever supplier or lookback changes.
  useEffect(() => {
    if (!supplier?.code) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const r = await api.get(
          `/price-explorer/supplier-items`,
          { params: { pcode: supplier.code, lookback_days: lookback } });
        if (cancelled) return;
        setData(r.data);
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.detail || err?.message || "Failed to load";
        setError(msg);
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [supplier?.code, lookback]);

  const rows = useMemo(() => {
    if (!data?.items) return [];
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? data.items.filter((r) => (r.icode || "").toLowerCase().includes(needle)
                              || (r.iname || "").toLowerCase().includes(needle))
      : data.items;
    const sorted = [...filtered].sort((a, b) => {
      const va = a[sortKey]; const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "desc" ? vb - va : va - vb;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sortDir === "desc" ? sb.localeCompare(sa) : sa.localeCompare(sb);
    });
    return sorted;
  }, [data, q, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir(key === "icode" || key === "iname" ? "asc" : "desc"); }
  };

  // CSV export — buyer-friendly for pasting into email / negotiation
  const downloadCSV = () => {
    if (!data?.items?.length) return;
    const header = ["Item Code", "Item Name", "UoM", "Last Rate (INR)", "Last Date",
                    "Lowest Rate (INR)", "Lowest Date", "Bills", "Total Qty", "Total Spend (INR)",
                    "SAP Contract"];
    const csv = [header, ...rows.map((r) => [
      r.icode, r.iname, r.unit,
      r.last_rate ?? "", r.last_date ?? "",
      r.lowest_rate ?? "", r.lowest_date ?? "",
      r.bill_count, r.total_qty, Math.round(r.total_spend || 0),
      r.has_sap_contract ? "Yes" : "No",
    ])].map((row) => row.map((c) => {
      const s = String(c ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `supplier-price-list-${supplier.code}-${lookback}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} rows`);
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowDown size={10} className="text-slate-300 inline-block ml-0.5" />;
    return sortDir === "desc"
      ? <ArrowDown size={10} className="text-cyan-700 inline-block ml-0.5" />
      : <ArrowUp   size={10} className="text-cyan-700 inline-block ml-0.5" />;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-cyan-800 to-cyan-700 text-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-1 sm:gap-2 mb-2 sm:mb-1">
            <Button variant="ghost" size="sm" onClick={() => nav("/price-explorer")}
                    className="text-white hover:bg-white/10 h-8 px-2"
                    data-testid="spl-back-pe">
              <ArrowLeft size={16} className="sm:mr-1" />
              <span className="hidden sm:inline">Price Explorer</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => nav("/home")}
                    className="text-white hover:bg-white/10 h-8 px-2"
                    data-testid="spl-back-home">
              <Home size={16} className="sm:mr-1" />
              <span className="hidden sm:inline">Home</span>
            </Button>
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold">Supplier Price List</h1>
            <p className="text-[11px] sm:text-xs text-cyan-100/90 leading-snug">
              Pick a supplier → every item they've billed us for, with actual purchasing prices from MS SQL
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-4">
        {/* Controls: supplier picker + lookback + filter */}
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-6">
                <Label className="text-fiori-caption text-slate-700 font-semibold mb-1 block">Supplier</Label>
                <SupplierPicker
                  value={supplier?.code}
                  name={supplier?.name}
                  onPick={(row) => setSupplier(row)}
                />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-fiori-caption text-slate-700 font-semibold mb-1 block">Lookback</Label>
                <div className="inline-flex bg-white border border-slate-300 rounded-md p-0.5 w-full"
                     role="tablist" data-testid="spl-lookback-picker">
                  {LOOKBACK_OPTIONS.map((o) => (
                    <button
                      key={o.d}
                      role="tab"
                      aria-selected={lookback === o.d}
                      onClick={() => setLookback(o.d)}
                      data-testid={`spl-lookback-${o.d}`}
                      className={`flex-1 px-1.5 py-1.5 text-[11px] font-semibold rounded-sm transition-colors ${
                        lookback === o.d
                          ? "bg-cyan-700 text-white"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}>
                      {o.d}d
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-3">
                <Label className="text-fiori-caption text-slate-700 font-semibold mb-1 block">Filter items</Label>
                <div className="flex items-center gap-2 border border-slate-300 rounded-md bg-white px-3 h-10">
                  <Search size={14} className="text-slate-400 shrink-0" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="code or name…"
                    className="h-8 border-0 focus-visible:ring-0 shadow-none px-0 text-sm"
                    data-testid="spl-filter-input"
                    disabled={!data?.items?.length}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Empty / loading / error states */}
        {!supplier && !loading && (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <div className="text-slate-500 text-sm" data-testid="spl-empty-state">
                Pick a supplier above to see their price list.
              </div>
            </CardContent>
          </Card>
        )}
        {loading && (
          <div className="text-sm text-slate-500 flex items-center gap-2 py-8 justify-center">
            <Loader2 size={14} className="animate-spin" /> Loading purchasing history…
          </div>
        )}
        {error && !loading && (
          <Card className="border-rose-200 bg-rose-50">
            <CardContent className="p-4 text-sm text-rose-800" data-testid="spl-error">
              {error}
            </CardContent>
          </Card>
        )}

        {/* Header stats + table */}
        {data && !loading && (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2 px-1">
              <div className="text-sm text-slate-700">
                <span className="font-bold text-slate-900">{data.item_count}</span>
                {" "}item{data.item_count === 1 ? "" : "s"} billed by{" "}
                <span className="mono font-bold text-slate-900">{data.supplier_code}</span>
                <span className="text-slate-500"> · {data.supplier_name}</span>
                <span className="text-slate-500"> · last {data.lookback_days} days</span>
                <span className="text-slate-500"> · total spend </span>
                <span className="font-bold text-slate-900">{inr(data.total_spend)}</span>
              </div>
              <Button size="sm" variant="outline" onClick={downloadCSV}
                      disabled={!rows.length} data-testid="spl-export-csv">
                <FileSpreadsheet size={12} className="mr-1" /> Export CSV
              </Button>
            </div>

            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="spl-items-table">
                    <thead>
                      <tr className="bg-slate-50 text-slate-700 border-b border-slate-200">
                        <Th onClick={() => toggleSort("icode")} testid="spl-sort-icode">
                          Item Code <SortIcon col="icode" />
                        </Th>
                        <Th onClick={() => toggleSort("iname")} testid="spl-sort-iname">
                          Name <SortIcon col="iname" />
                        </Th>
                        <Th className="text-right" onClick={() => toggleSort("last_rate")} testid="spl-sort-last">
                          Last Rate <SortIcon col="last_rate" />
                        </Th>
                        <Th className="text-right" onClick={() => toggleSort("last_date")} testid="spl-sort-lastdate">
                          Last Date <SortIcon col="last_date" />
                        </Th>
                        <Th className="text-right hidden md:table-cell" onClick={() => toggleSort("lowest_rate")} testid="spl-sort-low">
                          Lowest <SortIcon col="lowest_rate" />
                        </Th>
                        <Th className="text-right hidden md:table-cell" onClick={() => toggleSort("lowest_date")} testid="spl-sort-lowdate">
                          Lowest Date <SortIcon col="lowest_date" />
                        </Th>
                        <Th className="text-right hidden sm:table-cell" onClick={() => toggleSort("bill_count")} testid="spl-sort-bills">
                          Bills <SortIcon col="bill_count" />
                        </Th>
                        <Th className="text-right hidden md:table-cell" onClick={() => toggleSort("total_qty")} testid="spl-sort-qty">
                          Total Qty <SortIcon col="total_qty" />
                        </Th>
                        <Th className="text-right" onClick={() => toggleSort("total_spend")} testid="spl-sort-spend">
                          Total Spend <SortIcon col="total_spend" />
                        </Th>
                        <Th className="text-center" onClick={() => toggleSort("has_sap_contract")} testid="spl-sort-sap">
                          SAP <SortIcon col="has_sap_contract" />
                        </Th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={10} className="px-3 py-8 text-center text-slate-500 italic"
                              data-testid="spl-no-rows">
                            {q ? `No items match "${q}"` : "No purchases in this window"}
                          </td>
                        </tr>
                      )}
                      {rows.map((r) => (
                        <tr key={r.icode}
                            className="border-b border-slate-100 hover:bg-cyan-50/30"
                            data-testid={`spl-row-${r.icode}`}>
                          <td className="px-3 py-2 mono font-semibold text-slate-900 whitespace-nowrap">{r.icode}</td>
                          <td className="px-3 py-2 text-slate-700 max-w-[280px] truncate" title={r.iname}>{r.iname || "—"}</td>
                          <td className="px-3 py-2 text-right num font-semibold text-slate-900 whitespace-nowrap">
                            {inr(r.last_rate)}
                            {r.unit ? <span className="text-fiori-caption text-slate-400 ml-0.5">/{r.unit}</span> : null}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-600 whitespace-nowrap">{fmtDate(r.last_date)}</td>
                          <td className="px-3 py-2 text-right num text-slate-700 hidden md:table-cell whitespace-nowrap">{inr(r.lowest_rate)}</td>
                          <td className="px-3 py-2 text-right text-slate-500 hidden md:table-cell whitespace-nowrap">{fmtDate(r.lowest_date)}</td>
                          <td className="px-3 py-2 text-right hidden sm:table-cell">
                            <button
                              type="button"
                              onClick={() => setHistoryItem({ icode: r.icode, iname: r.iname, pcode: data.supplier_code })}
                              className="text-cyan-700 hover:text-cyan-900 font-semibold underline underline-offset-2 inline-flex items-center gap-0.5"
                              data-testid={`spl-bills-${r.icode}`}
                              title={`See the ${r.bill_count} billed receipt${r.bill_count === 1 ? "" : "s"}`}>
                              {r.bill_count}<ExternalLink size={9} />
                            </button>
                          </td>
                          <td className="px-3 py-2 text-right num text-slate-700 hidden md:table-cell">
                            {Number(r.total_qty || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </td>
                          <td className="px-3 py-2 text-right num font-semibold text-slate-900 whitespace-nowrap">{inr(r.total_spend)}</td>
                          <td className="px-3 py-2 text-center">
                            {r.has_sap_contract ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-fiori-caption font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200"
                                    data-testid={`spl-sap-yes-${r.icode}`}>Active</span>
                            ) : (
                              <span className="text-fiori-caption text-slate-400"
                                    data-testid={`spl-sap-no-${r.icode}`}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>

      <PriceHistoryDrawer
        item={historyItem}
        onClose={() => setHistoryItem(null)}
      />
    </div>
  );
}

// Compact clickable table-header cell — spelled out once so the JSX
// above stays scannable. Whitespace-nowrap keeps column widths honest.
function Th({ children, onClick, className = "", testid }) {
  return (
    <th onClick={onClick}
        data-testid={testid}
        className={`px-3 py-2 font-semibold cursor-pointer select-none hover:bg-slate-100 whitespace-nowrap ${className}`}>
      {children}
    </th>
  );
}
